import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Favorite,
  Map,
  AddCircleOutline,
  MailOutline,
  Settings,
  Search,
  Login,
  Menu as MenuIcon,
  Close,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { cloudKitService } from '../services/cloudkit';

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactElement;
  currentPath: string;
}

const navItems = [
  { to: '/', label: 'Shares', icon: <Search /> },
  { to: '/locations', label: 'Map', icon: <Map /> },
  { to: '/create', label: 'Share', icon: <AddCircleOutline /> },
  { to: '/messages', label: 'Messages', icon: <MailOutline /> },
  { to: '/developer', label: 'Dev', icon: <Settings /> },
];

function NavItem({ to, label, icon, currentPath }: NavItemProps) {
  const isActive = currentPath === to;

  return (
    <Button
      component={RouterLink}
      to={to}
      startIcon={icon}
      sx={{
        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.7)',
        backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
        borderRadius: 2,
        px: 2,
        py: 0.75,
        mx: 0.25,
        fontSize: '0.85rem',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
          color: '#ffffff',
        },
      }}
    >
      {label}
    </Button>
  );
}

export function NavBar() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userName, setUserName] = useState(cloudKitService.getUserName());
  const [userAvatarUrl, setUserAvatarUrl] = useState(cloudKitService.getUserAvatarUrl());

  useEffect(() => {
    const handleAuthChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ authenticated: boolean; userName: string; userAvatarUrl: string }>;
      setUserName(customEvent.detail.userName || '');
      setUserAvatarUrl(customEvent.detail.userAvatarUrl || '');
    };
    window.addEventListener('cloudkit-auth-change', handleAuthChange);
    return () => window.removeEventListener('cloudkit-auth-change', handleAuthChange);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Toolbar sx={{ px: { xs: 1.5, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Favorite sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: '#ffffff',
              }}
            >
              Share It!
            </Typography>
            <Chip
              label="beta"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                display: { xs: 'none', sm: 'flex' },
              }}
            />
          </Box>

          {/* Desktop navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navItems.map((item) => (
                <NavItem key={item.to} to={item.to} label={item.label} icon={item.icon} currentPath={location.pathname} />
              ))}
              <Box sx={{ ml: 2, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
                {userName ? (
                  <>
                    <Avatar
                      src={userAvatarUrl || undefined}
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: '#ffffff',
                      }}
                    >
                      {userName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography
                      variant="body2"
                      sx={{ color: '#ffffff', fontWeight: 500, whiteSpace: 'nowrap' }}
                    >
                      {userName}
                    </Typography>
                  </>
                ) : (
                  <Button
                    component={RouterLink}
                    to="/create"
                    startIcon={<Login />}
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.85rem',
                      '&:hover': { color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.08)' },
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ color: '#ffffff' }}
              aria-label="open menu"
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(180deg, #6366f1 0%, #4f46e5 100%)',
            color: '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#ffffff' }}>
            <Close />
          </IconButton>
        </Box>

        {/* User display in drawer */}
        <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {userName ? (
            <>
              <Avatar
                src={userAvatarUrl || undefined}
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: '1rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: '#ffffff',
                }}
              >
                {userName.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {userName}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Not signed in
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />

        <List sx={{ pt: 1 }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.to}
                  sx={{
                    py: 1.5,
                    px: 3,
                    backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? '#ffffff' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.85)',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {!userName && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/create"
                  sx={{ py: 1.5, px: 3, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                    <Login />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sign In"
                    primaryTypographyProps={{ fontWeight: 500, color: 'rgba(255,255,255,0.85)' }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
      </Drawer>
    </>
  );
}
